/**
 * Provincias, municipios y búsqueda por código postal (MVP-02B §3).
 *
 * Concentra las tres trampas que rompían el formulario:
 *
 * 1. **Carga independiente.** Provincias y direcciones se pedían juntas con
 *    `Promise.all`: si fallaba `/api/direcciones/`, `setProvincias` no llegaba
 *    a ejecutarse y el desplegable de provincia se quedaba vacío sin que nada
 *    lo dijera. Aquí cada recurso tiene su propio ciclo de vida.
 * 2. **Carrera del código postal.** El lookup fijaba provincia y municipio a la
 *    vez, pero la lista de municipios de esa provincia todavía estaba viajando,
 *    así que el campo se pintaba vacío. `ensureMunicipios` devuelve una promesa
 *    para poder esperar a la lista antes de seleccionar.
 * 3. **Municipios duplicados.** El dataset guarda una fila por par
 *    (municipio, CP): Madrid son 63 filas. La lista para elegir se deduplica
 *    por nombre; la lista cruda se conserva para resolver códigos postales.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { fetchMunicipios, fetchProvincias, lookupCodigoPostal } from "./api.js";

const recursoInicial = { state: "loading", data: [], error: null };

const comoRecurso = (data) => ({
    state: data.length ? "ready" : "empty",
    data,
    error: null,
});

export function useGeografia(provinciaId) {
    const [provincias, setProvincias] = useState(recursoInicial);
    const [municipios, setMunicipios] = useState({ state: "empty", data: [], error: null });

    // Token de petición: si el usuario cambia de provincia dos veces seguidas,
    // la respuesta lenta de la primera no debe pisar a la segunda.
    const municipiosToken = useRef(0);
    const cache = useRef(new Map());

    const cargarProvincias = useCallback(() => {
        setProvincias((prev) => ({ ...prev, state: "loading", error: null }));
        return fetchProvincias()
            .then((data) => setProvincias(comoRecurso(data)))
            .catch((error) => setProvincias({ state: "error", data: [], error }));
    }, []);

    useEffect(() => {
        cargarProvincias();
    }, [cargarProvincias]);

    /**
     * Carga los municipios de una provincia y resuelve con la lista.
     *
     * Devolver la lista permite que quien llama espere antes de seleccionar un
     * municipio, en vez de fijar un id que todavía no está en la lista.
     */
    const ensureMunicipios = useCallback(async (id) => {
        if (!id) {
            municipiosToken.current += 1;
            setMunicipios({ state: "empty", data: [], error: null });
            return [];
        }

        if (cache.current.has(id)) {
            const cacheada = cache.current.get(id);
            municipiosToken.current += 1;
            setMunicipios(comoRecurso(cacheada));
            return cacheada;
        }

        const token = (municipiosToken.current += 1);
        setMunicipios({ state: "loading", data: [], error: null });
        try {
            const data = await fetchMunicipios(id);
            cache.current.set(id, data);
            if (municipiosToken.current === token) setMunicipios(comoRecurso(data));
            return data;
        } catch (error) {
            if (municipiosToken.current === token) {
                setMunicipios({ state: "error", data: [], error });
            }
            return [];
        }
    }, []);

    useEffect(() => {
        ensureMunicipios(provinciaId);
    }, [provinciaId, ensureMunicipios]);

    /** Municipios para elegir: uno por nombre, con todos sus CP asociados. */
    const municipiosUnicos = useMemo(() => {
        const porNombre = new Map();
        for (const municipio of municipios.data) {
            const existente = porNombre.get(municipio.nombre);
            if (existente) {
                existente.codigos_postales.push(municipio.cod_postal);
            } else {
                porNombre.set(municipio.nombre, {
                    ...municipio,
                    codigos_postales: [municipio.cod_postal],
                });
            }
        }
        return [...porNombre.values()];
    }, [municipios.data]);

    /** Códigos postales válidos del municipio elegido, para no pisar uno bueno. */
    const codigosPostalesDe = useCallback(
        (municipioId) => {
            const elegido = municipios.data.find((m) => Number(m.id) === Number(municipioId));
            if (!elegido) return [];
            return [
                ...new Set(
                    municipios.data
                        .filter((m) => m.nombre === elegido.nombre)
                        .map((m) => m.cod_postal),
                ),
            ];
        },
        [municipios.data],
    );

    /**
     * Busca un código postal y clasifica el resultado.
     *
     * No decide por el usuario: devolver `multiple` obliga a la UI a preguntar
     * en lugar de coger `data[0]` en silencio, que con códigos compartidos por
     * decenas de municipios elegía casi siempre el equivocado.
     */
    const buscarCodigoPostal = useCallback(async (codigoPostal) => {
        try {
            const coincidencias = await lookupCodigoPostal(codigoPostal);
            if (!coincidencias.length) return { kind: "none", coincidencias: [] };
            if (coincidencias.length === 1) {
                return { kind: "single", coincidencias, coincidencia: coincidencias[0] };
            }
            return { kind: "multiple", coincidencias };
        } catch (error) {
            return { kind: "error", coincidencias: [], error };
        }
    }, []);

    return {
        provincias,
        municipios,
        municipiosUnicos,
        recargarProvincias: cargarProvincias,
        recargarMunicipios: () => ensureMunicipios(provinciaId),
        ensureMunicipios,
        codigosPostalesDe,
        buscarCodigoPostal,
    };
}
